/**
 * Video players — ESM port of the legacy modules/video-players.js.
 *
 * Scans the page for video iframes / .js-video-player divs, lazy-imports
 * Plyr only when at least one is present, and instantiates a player per
 * element. Native DOM throughout; listens for the lifecycle events
 * `ep:ready` / `ep:change` dispatched by ESM app-config and router.
 *
 * Self-initializes — no init() call from app-config. Removed from the
 * modules array there.
 *
 * AI: Mostly written by AI
 * Date: 2026-04-25
 * Model: Claude Opus 4.7
 * Updated: 2026-10-05 (Claude Opus 5.5) - Plyr's stylesheet links ahead of the theme's
 *
 * [VideoPlayers]
 */

import { plyrControls } from './plyr-controls.js';
import { log } from './log.js';
import { appGet } from './app-get.js';

const PLAYER_CLASS = 'js-video-player';
const CONTENT_RESPONSIVE_SELECTOR = '.richtext iframe[src*="vimeo"], .richtext iframe[src*="youtube"]';
const FEATURE_SELECTOR = `#homepage-features .${PLAYER_CLASS}`;

let plyrPromise = null;
let initPlayers = {};

// Plyr's stylesheet ships as its own asset (theme/css/video-player.scss) and is linked
// here the first time a page needs a player — once per module instance, like the
// import below; Turbo keeps <head> links across navigations. Without the URL (app
// not booted) Plyr still initialises — unstyled controls beat no player.
// It goes ahead of the first stylesheet, so a theme rule that re-styles the player
// wins a specificity tie on every path, as it did when Plyr sat early in style.css:
// appended, it came last on a fresh load (Turbo appends a later page's bundle after
// it), and the homepage feature's centred play button lost to Plyr's bottom-left one.
const linkPlyrCss = () => {
  const href = appGet('assets.plyrCssUrl');
  if (!href) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.insertBefore(link, document.head.querySelector('link[rel="stylesheet"]'));
  log('[VideoPlayers] plyr.css linked');
};

const loadPlyr = () => {
  if (!plyrPromise) {
    linkPlyrCss();
    plyrPromise = import('plyr').then((m) => m.default);
  }
  return plyrPromise;
};

const setVideoPlayersState = () => {
  if (window.app && typeof window.app.set === 'function') {
    window.app.set('videoPlayers', initPlayers);
  }
};

const buildVideoOptions = () => ({
  enabled: true,
  controls: plyrControls,
  iconUrl: appGet('assets.plyrIconUrl'),
  autoplay: false,
  volume: 1,
  muted: false,
  hideControls: true,
  resetOnEnd: true,
});

function getVimeoId(str) {
  if (str.indexOf('#') > -1) str = str.split('#')[0];
  if (str.indexOf('?') > -1 && str.indexOf('clip_id=') === -1) str = str.split('?')[0];

  let id;
  let arr;

  if (/https?:\/\/vimeo\.com\/[0-9]+$|https?:\/\/player\.vimeo\.com\/video\/[0-9]+$|https?:\/\/vimeo\.com\/channels|groups|album/igm.test(str)) {
    arr = str.split('/');
    if (arr && arr.length) id = arr.pop();
  } else if (/clip_id=/igm.test(str)) {
    arr = str.split('clip_id=');
    if (arr && arr.length) id = arr[1].split('&')[0];
  }

  return id;
}

function stripParameters(str) {
  if (str.indexOf('?') > -1) return str.split('?')[0];
  if (str.indexOf('/') > -1) return str.split('/')[0];
  return str;
}

function getYoutubeId(str) {
  const shortcode = /youtube:\/\/|https?:\/\/youtu\.be\//g;
  if (shortcode.test(str)) return stripParameters(str.split(shortcode)[1]);

  const inlinev = /\/v\/|\/vi\//g;
  if (inlinev.test(str)) return stripParameters(str.split(inlinev)[1]);

  const parameterv = /v=|vi=/g;
  if (parameterv.test(str)) return str.split(parameterv)[1].split('&')[0];

  const parameterwebp = /\/an_webp\//g;
  if (parameterwebp.test(str)) return stripParameters(str.split(parameterwebp)[1]);

  const embedreg = /\/embed\//g;
  if (embedreg.test(str)) return stripParameters(str.split(embedreg)[1]);

  const userreg = /\/user\//g;
  if (userreg.test(str)) return stripParameters(str.split('/').pop());

  const attrreg = /\/attribution_link\?.*v%3D([^%&]*)(%26|&|$)/;
  if (attrreg.test(str)) return str.match(attrreg)[1];
}

async function createVideoPlayer(player, options, id) {
  const Plyr = await loadPlyr();
  const newPlayer = new Plyr(player, options);
  const feature = player.closest('.block-feature-video');
  const isFeature = !!feature;

  newPlayer.on('volumechange', (event) => {
    const mutedClass = 'plyr__control--pressed';
    const instance = event.detail.plyr;
    const volume = instance.media.volume;
    const muted = instance.media.muted;
    const muteButton = event.target.querySelector('button[data-plyr="mute"]');
    if (!muteButton) return;
    if (volume === 0 || muted === true) muteButton.classList.add(mutedClass);
    else muteButton.classList.remove(mutedClass);
  });

  newPlayer.on('ended', () => {
    newPlayer.stop();
    newPlayer.currentTime = 0;
    if (isFeature) {
      feature.classList.remove('video-is-playing');
      hideOnPlayElements(feature, false);
    }
  });

  if (isFeature) {
    newPlayer.on('play', () => {
      feature.classList.add('video-is-playing');
      hideOnPlayElements(feature, true);
    });
    newPlayer.on('pause', () => {
      newPlayer.stop();
      feature.classList.remove('video-is-playing');
      hideOnPlayElements(feature, false);
    });
  }

  initPlayers[id] = newPlayer;
  setVideoPlayersState();
}

function hideOnPlayElements(feature, hide) {
  const wrapper = feature.closest('#homepage-features');
  if (!wrapper) return;
  wrapper.querySelectorAll('.js-hide-on-play').forEach((el) => {
    el.style.display = hide ? 'none' : '';
  });
}

function createContentPlayer(settings) {
  let player = settings.element;
  if (player.parentElement && player.parentElement.tagName === 'P') {
    const p = player.parentElement;
    p.parentNode.insertBefore(player, p);
    p.remove();
  }
  const replacement = document.createElement('div');
  replacement.id = `video-player-${settings.id}`;
  replacement.className = `video-player ${PLAYER_CLASS}`;
  replacement.setAttribute('data-plyr-provider', settings.provider);
  replacement.setAttribute('data-plyr-embed-id', settings.id);
  player.replaceWith(replacement);
  createVideoPlayer(replacement, settings.options, settings.id);
}

function initPlayer(settings) {
  if (settings.type === 'iframe') createContentPlayer(settings);
  else if (settings.type === 'div') createVideoPlayer(settings.element, settings.options, settings.id);
}

function scanAndInit(event) {
  if (event && event.type === 'ep:change') initPlayers = {};
  setVideoPlayersState();

  const players = document.querySelectorAll(`${CONTENT_RESPONSIVE_SELECTOR}, ${FEATURE_SELECTOR}`);
  if (players.length === 0) return;

  const videoOptions = buildVideoOptions();

  players.forEach((player) => {
    const isIframe = player.tagName === 'IFRAME';
    const type = isIframe ? 'iframe' : 'div';
    let provider;
    let videoId;

    if (isIframe) {
      const src = player.getAttribute('src') || '';
      provider = src.indexOf('player.vimeo.com') > -1 ? 'vimeo' : 'youtube';
      videoId = provider === 'vimeo' ? getVimeoId(src) : getYoutubeId(src);
    } else {
      provider = player.getAttribute('data-plyr-provider');
      videoId = player.getAttribute('data-plyr-embed-id');
    }

    if (!videoId || (provider !== 'vimeo' && provider !== 'youtube')) return;

    const options = { ...videoOptions };
    // Posters are rendered server-side by the Liquid template
    // (block-feature-video.liquid) onto data-poster — no client-side
    // thumbnail fetch. Iframes pasted in richtext have no data-poster
    // and fall back to Plyr's native iframe-provider thumbnail.
    const poster = isIframe ? null : player.getAttribute('data-poster');
    if (poster) options.poster = poster;

    initPlayer({ element: player, options, provider, id: videoId, type });
  });
}

// ep:ready fires after app-config.start() finishes; ep:change fires
// from core/router.esm.js on every Turbo Drive navigation. Reset the
// per-element guard cache on ep:change so re-rendered video tiles in
// the new DOM get re-initialised.
document.addEventListener('ep:ready', scanAndInit);
document.addEventListener('ep:change', scanAndInit);
log('[VideoPlayers] registered');
